import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  ArrowLeft, Loader2, UserPlus, FileDown, GraduationCap, History, Plus, Trash2,
  Pencil, AlertTriangle, ClipboardCheck, ChevronDown,
} from 'lucide-react';
import { Topbar } from '@/components/layout/Topbar';
import { api, formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { succLabel, riskTone, planTone, auditLine } from '@/lib/successionLabels';
import { SuccGauge } from '@/components/client/SuccGauge';
import { SuccContinuityTab } from '@/components/client/SuccContinuityTab';
import { SuccIdealTab } from '@/components/client/SuccIdealTab';

const READINESS = ['ready_now', 'ready_1y', 'ready_2y', 'development_required'];
const QUADRANTS = ['technical', 'education', 'experience', 'critical_experiences'];
const RATINGS = ['meets', 'partial', 'not_met', 'no_evidence'];

function AssessmentEditor({ position, successor, isES, reload }) {
  const x = (es, en) => (isES ? es : en);
  const quads = (position.ideal_scenario || {}).quadrants || {};
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const hasReqs = QUADRANTS.some((q) => ((quads[q] || {}).requirements || []).length);
  if (!hasReqs) return <p className="text-xs text-muted-foreground italic">{x('Define primero el escenario ideal para evaluar a este sucesor.', 'Define the ideal scenario first to assess this successor.')}</p>;
  const current = successor.assessments || {};
  const save = async () => {
    if (!Object.keys(draft).length) return;
    setBusy(true);
    try {
      await api.put(`/client/succession/positions/${position.id}/successors/${successor.id}/assessments`, { assessments: draft });
      toast.success(x('Evaluación guardada.', 'Assessment saved.'));
      setDraft({}); reload();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid={`succ-assess-${successor.id}`}>
      {QUADRANTS.map((q) => {
        const reqs = (quads[q] || {}).requirements || [];
        if (!reqs.length) return null;
        return (
          <div key={q}>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">{succLabel(isES, 'quadrant', q)}</p>
            {reqs.map((r) => {
              const val = draft[r.id]?.rating || current[r.id]?.rating || 'no_evidence';
              return (
                <div key={r.id} className="flex items-center justify-between gap-2 py-1 text-sm">
                  <span className="min-w-0 leading-snug">{r.name}
                    {r.mandatory && <Badge variant="outline" className="ml-1.5 text-[9px] bg-destructive/10 text-destructive border-destructive/30">{x('Habilitante', 'Enabling')}</Badge>}
                  </span>
                  <Select value={val} onValueChange={(v) => setDraft({ ...draft, [r.id]: { rating: v } })}>
                    <SelectTrigger className="w-44 h-8 text-xs shrink-0" data-testid={`succ-rate-${r.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {RATINGS.map((rt) => <SelectItem key={rt} value={rt}>{succLabel(isES, 'rating', rt)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              );
            })}
          </div>
        );
      })}
      <Button size="sm" onClick={save} disabled={busy || !Object.keys(draft).length} className="bg-gradient-primary hover:opacity-90" data-testid={`succ-assess-save-${successor.id}`}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : x('Guardar evaluación', 'Save assessment')}
      </Button>
    </div>
  );
}

export default function ClientSuccessionPosition() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isES = !(i18n.language || '').toLowerCase().startsWith('en');
  const x = (es, en) => (isES ? es : en);
  const [p, setP] = useState(null);
  const [history, setHistory] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [devOpen, setDevOpen] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(null);
  const [reviewForm, setReviewForm] = useState({ readiness: '', comment: '' });
  const [succForm, setSuccForm] = useState({ user_id: '', readiness: 'development_required', notes: '' });
  const [devForm, setDevForm] = useState({ objective: '', action: '', competency: '', responsible_user_id: '', target_date: '', source_quadrant: '', source_requirement: '' });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/client/succession/positions/${id}`).then((r) => setP(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get(`/client/succession/positions/${id}/history`).then((r) => setHistory(r.data)).catch(() => {});
  }, [id]);
  useEffect(() => {
    load();
    api.get('/client/employees').then((r) => setEmployees(r.data?.items || r.data || [])).catch(() => {});
  }, [load]);

  const savePosition = async () => {
    setBusy(true);
    try {
      await api.patch(`/client/succession/positions/${id}`, editForm);
      toast.success(x('Posición actualizada.', 'Position updated.'));
      setEditOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const addSuccessor = async () => {
    if (!succForm.user_id) { toast.error(x('Selecciona un colaborador.', 'Select an employee.')); return; }
    setBusy(true);
    try {
      await api.post(`/client/succession/positions/${id}/successors`, succForm);
      toast.success(x('Sucesor potencial agregado.', 'Potential successor added.'));
      setAddOpen(false); setSuccForm({ user_id: '', readiness: 'development_required', notes: '' }); load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const submitReview = async () => {
    setBusy(true);
    try {
      await api.post(`/client/succession/positions/${id}/successors/${reviewOpen}/review`, reviewForm);
      toast.success(x('Revisión de preparación registrada.', 'Readiness review recorded.'));
      setReviewOpen(null); load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const removeSuccessor = async (sid) => {
    try {
      await api.delete(`/client/succession/positions/${id}/successors/${sid}`);
      toast.success(x('Sucesor retirado.', 'Successor removed.')); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const addDev = async () => {
    if (devForm.objective.trim().length < 3 || devForm.action.trim().length < 3) {
      toast.error(x('Objetivo y acción son obligatorios.', 'Objective and action are required.')); return;
    }
    setBusy(true);
    try {
      await api.post(`/client/succession/positions/${id}/successors/${devOpen}/plan`, devForm);
      toast.success(x('Acción de desarrollo creada.', 'Development action created.'));
      setDevOpen(null);
      setDevForm({ objective: '', action: '', competency: '', responsible_user_id: '', target_date: '', source_quadrant: '', source_requirement: '' });
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const sendToTT = async (sid, iid) => {
    try {
      await api.post(`/client/succession/positions/${id}/successors/${sid}/plan/${iid}/send-to-talenttrain`);
      toast.success(x('Enviado a TalentTrain.', 'Sent to TalentTrain.')); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const exportDoc = async (fmt) => {
    try {
      const r = await api.get(`/client/succession/positions/${id}/export/${fmt}`, { params: { lang: isES ? 'es' : 'en' }, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `succession-plan.${fmt}`; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openEditPosition = () => {
    setEditForm({
      position_title: p.position_title, department_name: p.department_name || '',
      incumbent_user_id: p.incumbent_user_id || '', incumbent_name: p.incumbent_name || '',
      vacant: !p.incumbent_name, criticality: p.criticality, risk: p.risk,
      horizon: p.horizon, business_impact: p.business_impact || '', notes: p.notes || '',
    });
    setEditOpen(true);
  };

  const fmtD = (d) => (d ? d : '—');

  if (!p) return <div className="min-h-[50vh] flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="min-h-screen" data-testid="succ-position-detail">
      <Topbar
        title={p.position_title}
        subtitle={`${p.incumbent_name || x('Vacante / Sin titular', 'Vacant / No incumbent')}${p.department_name ? ` · ${p.department_name}` : ''}`}
        leading={(
          <Button variant="ghost" size="sm" onClick={() => navigate('/client/succession')} className="gap-1.5" data-testid="succ-back-btn">
            <ArrowLeft className="h-4 w-4" /> Succession
          </Button>
        )}
        actions={(
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={openEditPosition} className="gap-1.5" data-testid="succ-edit-position-btn"><Pencil className="h-4 w-4" /> {x('Editar', 'Edit')}</Button>
            <Button variant="outline" size="sm" onClick={() => exportDoc('pdf')} className="gap-1.5" data-testid="succ-export-pdf"><FileDown className="h-4 w-4" /> PDF</Button>
            <Button variant="outline" size="sm" onClick={() => exportDoc('docx')} className="gap-1.5" data-testid="succ-export-docx"><FileDown className="h-4 w-4" /> Word</Button>
          </div>
        )}
      />
      <div className="p-6 lg:p-8 space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="succ-position-meta">
        {[[x('Criticidad', 'Criticality'), succLabel(isES, 'criticality', p.criticality)],
          [x('Riesgo de sucesión', 'Succession risk'), <Badge key="r" variant="outline" className={riskTone[p.risk]}>{succLabel(isES, 'risk', p.risk)}</Badge>],
          [x('Horizonte', 'Horizon'), succLabel(isES, 'horizon', p.horizon)],
          [x('Cobertura', 'Coverage'), succLabel(isES, 'coverage', p.coverage)],
          [x('Estado del plan', 'Plan status'), <Badge key="p" variant="outline" className={planTone[p.plan_status]}>{succLabel(isES, 'plan', p.plan_status)}</Badge>]].map(([label, val], i) => (
          <Card key={i} className="p-3 shadow-card">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
            <div className="text-sm font-semibold mt-1">{val}</div>
          </Card>
        ))}
      </div>

      {p.readiness_gap && (
        <Card className="p-4 shadow-card border-warning/40 bg-warning/5" data-testid="succ-readiness-gap">
          <p className="text-sm font-semibold flex items-center gap-2 text-warning"><AlertTriangle className="h-4 w-4" /> {x('Brecha de preparación', 'Readiness gap')}</p>
          <p className="text-sm mt-1 text-foreground/85">
            {x('El horizonte esperado de la posición no está cubierto por la preparación actual de los sucesores. El horizonte de preparación esperado requiere revisión.',
               'The position\u2019s expected horizon is not covered by the successors\u2019 current readiness. The expected readiness horizon requires review.')}
          </p>
        </Card>
      )}

      {p.business_impact && (
        <Card className="p-4 shadow-card" data-testid="succ-impact">
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">{x('Impacto en el negocio', 'Business impact')}</p>
          <p className="text-sm leading-relaxed">{p.business_impact}</p>
        </Card>
      )}

      <Tabs defaultValue="continuity" className="w-full">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="continuity" data-testid="succ-tab-continuity">{x('Perfil de Continuidad', 'Continuity Profile')}</TabsTrigger>
          <TabsTrigger value="ideal" data-testid="succ-tab-ideal">{x('Escenario ideal', 'Ideal scenario')}</TabsTrigger>
          <TabsTrigger value="successors" data-testid="succ-tab-successors">{x('Sucesores', 'Successors')} ({(p.successors || []).length})</TabsTrigger>
          <TabsTrigger value="history" data-testid="succ-tab-history">{x('Historial', 'History')}</TabsTrigger>
        </TabsList>

        <TabsContent value="continuity" className="mt-4">
          <SuccContinuityTab position={p} isES={isES} reload={load} />
        </TabsContent>
        <TabsContent value="ideal" className="mt-4">
          <SuccIdealTab position={p} isES={isES} reload={load} />
        </TabsContent>

        <TabsContent value="successors" className="mt-4 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-semibold">{x('Sucesores potenciales', 'Potential successors')} ({(p.successors || []).length})</h3>
            <Button size="sm" onClick={() => setAddOpen(true)} className="bg-gradient-primary hover:opacity-90 gap-1.5" data-testid="succ-add-successor-btn">
              <UserPlus className="h-4 w-4" /> {x('Agregar sucesor', 'Add successor')}
            </Button>
          </div>
          {(p.successors || []).length === 0 ? (
            <Card className="p-8 shadow-card"><p className="text-sm text-muted-foreground text-center" data-testid="succ-successors-empty">
              {x('Esta posición crítica no tiene sucesores potenciales. Identifica al menos uno para reducir el riesgo de continuidad.',
                 'This critical position has no potential successors. Identify at least one to reduce continuity risk.')}
            </p></Card>
          ) : (p.successors || []).map((s) => (
            <Card key={s.id} className="p-4 shadow-card space-y-3" data-testid={`succ-successor-${s.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <p className="font-semibold">{s.name}</p>
                  <p className="text-xs text-muted-foreground">{s.position || ''}{s.department_name ? ` · ${s.department_name}` : ''}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline">{succLabel(isES, 'readiness', s.readiness)}</Badge>
                  <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs"
                    onClick={() => { setReviewOpen(s.id); setReviewForm({ readiness: s.readiness, comment: '' }); }} data-testid={`succ-review-${s.id}`}>
                    <ClipboardCheck className="h-3.5 w-3.5" /> {x('Revisar preparación', 'Review readiness')}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => removeSuccessor(s.id)} data-testid={`succ-remove-${s.id}`}><Trash2 className="h-4 w-4 text-muted-foreground" /></Button>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs" data-testid={`succ-dates-${s.id}`}>
                {[[x('Designación', 'Designated'), fmtD(s.designated_at)],
                  [x('Objetivo de preparación', 'Readiness target'), fmtD(s.target_readiness_date)],
                  [x('Próxima revisión', 'Next review'), fmtD(s.next_review_date)],
                  [x('Última revisión', 'Last review'), fmtD(s.last_review_at)]].map(([l, v], i) => (
                  <div key={i} className="rounded-lg bg-muted/40 px-2.5 py-1.5">
                    <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{l}</p>
                    <p className="font-medium">{v}</p>
                  </div>
                ))}
              </div>
              {(s.review_overdue || s.target_reached || (s.other_nominations || []).length > 0) && (
                <div className="space-y-1">
                  {s.review_overdue && (
                    <p className="text-xs text-warning flex items-center gap-1.5" data-testid={`succ-warn-review-${s.id}`}>
                      <AlertTriangle className="h-3.5 w-3.5" /> {x('Revisión de preparación vencida — se requiere evaluación.', 'Readiness review overdue — an evaluation is required.')}
                    </p>
                  )}
                  {s.target_reached && (
                    <p className="text-xs text-warning flex items-center gap-1.5" data-testid={`succ-warn-target-${s.id}`}>
                      <AlertTriangle className="h-3.5 w-3.5" /> {x('El horizonte de preparación esperado se ha alcanzado y requiere revisión.', 'The expected readiness horizon has been reached and requires review.')}
                    </p>
                  )}
                  {(s.other_nominations || []).length > 0 && (
                    <p className="text-xs text-info flex items-center gap-1.5 flex-wrap" data-testid={`succ-warn-concentration-${s.id}`}>
                      <AlertTriangle className="h-3.5 w-3.5" />
                      {x(`También nominado/a para ${s.other_nominations.length} posición(es) crítica(s) adicional(es):`, `Also nominated for ${s.other_nominations.length} additional critical position(s):`)}
                      {s.other_nominations.map((o) => (
                        <Link key={o.id} to={`/client/succession/${o.id}`} className="underline underline-offset-2">{o.position_title}</Link>
                      ))}
                    </p>
                  )}
                </div>
              )}
              <div className="flex flex-wrap items-start gap-4 pt-1" data-testid={`succ-gauges-${s.id}`}>
                {QUADRANTS.map((q) => {
                  const a = (s.aptitudes || {})[q] || {};
                  if (!a.requirements_total) return null;
                  return <SuccGauge key={q} value={a.aptitude} coverage={a.evidence_coverage} band={a.band}
                    label={succLabel(isES, 'quadrant', q)} testId={`succ-gauge-${q}-${s.id}`} />;
                })}
                {!QUADRANTS.some((q) => ((s.aptitudes || {})[q] || {}).requirements_total) && (
                  <p className="text-xs text-muted-foreground italic">{x('Define el escenario ideal para habilitar los tacómetros de aptitud.', 'Define the ideal scenario to enable aptitude gauges.')}</p>
                )}
              </div>
              {QUADRANTS.some((q) => (((s.aptitudes || {})[q] || {}).mandatory_pending || []).length) && (
                <p className="text-xs text-destructive flex items-center gap-1.5 flex-wrap" data-testid={`succ-mandatory-${s.id}`}>
                  <AlertTriangle className="h-3.5 w-3.5" /> {x('Requisito habilitante pendiente:', 'Enabling requirement pending:')}
                  {QUADRANTS.flatMap((q) => (((s.aptitudes || {})[q] || {}).mandatory_pending || []).map((m) => m.name)).join(' · ')}
                </p>
              )}
              {(s.priority_gaps || []).length > 0 && (
                <div className="rounded-lg border border-warning/30 bg-warning/5 p-3" data-testid={`succ-gaps-${s.id}`}>
                  <p className="text-xs font-semibold mb-1">{x('Brechas prioritarias', 'Priority gaps')}</p>
                  <div className="space-y-1">
                    {s.priority_gaps.map((g, i) => (
                      <div key={i} className="flex items-center justify-between gap-2 text-xs">
                        <span>{g.name} <span className="text-muted-foreground">({succLabel(isES, 'quadrant', g.quadrant)} · {succLabel(isES, 'rating', g.rating)})</span></span>
                        <Button variant="outline" size="sm" className="h-6 px-2 text-[11px] gap-1"
                          onClick={() => { setDevOpen(s.id); setDevForm({ ...devForm, objective: g.name, source_quadrant: g.quadrant, source_requirement: g.name }); }}
                          data-testid={`succ-gap-dev-${s.id}-${i}`}>
                          <Plus className="h-3 w-3" /> {x('Acción de desarrollo', 'Development action')}
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <Collapsible>
                <CollapsibleTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-1.5" data-testid={`succ-assess-toggle-${s.id}`}>
                    <ClipboardCheck className="h-4 w-4" /> {x('Evaluar requisitos', 'Assess requirements')} <ChevronDown className="h-3.5 w-3.5" />
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="pt-3">
                  <AssessmentEditor position={p} successor={s} isES={isES} reload={load} />
                </CollapsibleContent>
              </Collapsible>
              {(s.development_plan || []).length > 0 && (
                <div className="rounded-lg border border-border/60 divide-y">
                  {s.development_plan.map((it) => (
                    <div key={it.id} className="p-3 flex items-start justify-between gap-3 flex-wrap text-sm" data-testid={`succ-dev-${it.id}`}>
                      <div className="min-w-0">
                        <p className="font-medium">{it.objective}</p>
                        <p className="text-xs text-muted-foreground">{it.action}{it.responsible ? ` · ${it.responsible}` : ''}{it.target_date ? ` · ${it.target_date}` : ''}
                          {it.source_quadrant ? ` · ${succLabel(isES, 'quadrant', it.source_quadrant)}` : ''}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">{succLabel(isES, 'actionStatus', it.status)}</Badge>
                        {it.sent_to_talenttrain ? (
                          <Badge variant="outline" className="bg-info/10 text-info border-info/30">TalentTrain ✓</Badge>
                        ) : (
                          <Button variant="outline" size="sm" className="h-7 gap-1 text-xs" onClick={() => sendToTT(s.id, it.id)} data-testid={`succ-send-tt-${it.id}`}>
                            <GraduationCap className="h-3.5 w-3.5" /> {x('Enviar a TalentTrain', 'Send to TalentTrain')}
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setDevOpen(s.id)} data-testid={`succ-add-dev-${s.id}`}>
                <Plus className="h-4 w-4" /> {x('Acción de desarrollo', 'Development action')}
              </Button>
            </Card>
          ))}
          <p className="text-[11px] text-muted-foreground" data-testid="succ-nomination-note">
            {x('La designación como sucesor/a potencial es una herramienta de planificación: no constituye promoción, nombramiento ni garantía de empleo. Los tacómetros de aptitud apoyan el juicio humano; no determinan la preparación automáticamente.',
               'A potential-successor nomination is a planning tool: it is not a promotion, appointment or employment guarantee. Aptitude gauges support human judgment; they never determine readiness automatically.')}
          </p>
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          <Card className="p-4 shadow-card" data-testid="succ-history">
            <h4 className="text-sm font-semibold flex items-center gap-2 mb-2"><History className="h-4 w-4" /> {x('Historial', 'History')}</h4>
            {history.length === 0 ? <p className="text-sm text-muted-foreground">—</p> : history.slice(0, 30).map((h, i) => (
              <p key={i} className="text-xs py-1.5 border-b last:border-0">
                <span className="text-foreground/90 font-medium">{auditLine(isES, h)}</span>
                <span className="text-muted-foreground">{' · '}{h.actor_name}{' · '}{(h.timestamp || '').slice(0, 10)}</span>
              </p>
            ))}
          </Card>
        </TabsContent>
      </Tabs>

      {/* edit position */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-[900px] max-h-[85vh] overflow-y-auto" data-testid="succ-edit-dialog">
          <DialogHeader><DialogTitle>{x('Editar posición crítica', 'Edit critical position')}</DialogTitle></DialogHeader>
          {editForm && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2 space-y-1.5">
                <Label>{x('Posición', 'Position')}</Label>
                <Input data-testid="succ-edit-title" value={editForm.position_title} onChange={(e) => setEditForm({ ...editForm, position_title: e.target.value })} />
              </div>
              <div className="sm:col-span-2 space-y-1.5">
                <Label>{x('Titular actual', 'Current incumbent')}</Label>
                <Select value={editForm.vacant ? '__vacant__' : (editForm.incumbent_user_id || '__manual__')}
                  onValueChange={(v) => {
                    if (v === '__vacant__') setEditForm({ ...editForm, vacant: true, incumbent_user_id: '', incumbent_name: '' });
                    else if (v === '__manual__') setEditForm({ ...editForm, vacant: false, incumbent_user_id: '' });
                    else {
                      const u = employees.find((emp) => emp.id === v);
                      setEditForm({ ...editForm, vacant: false, incumbent_user_id: v, incumbent_name: u?.name || '', department_name: editForm.department_name || u?.department_name || '' });
                    }
                  }}>
                  <SelectTrigger data-testid="succ-edit-incumbent"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__vacant__">{x('Vacante / Sin titular', 'Vacant / No incumbent')}</SelectItem>
                    {employees.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                    {!editForm.vacant && !editForm.incumbent_user_id && editForm.incumbent_name && (
                      <SelectItem value="__manual__">{editForm.incumbent_name}</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>{x('Departamento', 'Department')}</Label>
                <Input data-testid="succ-edit-dept" value={editForm.department_name} onChange={(e) => setEditForm({ ...editForm, department_name: e.target.value })} />
              </div>
              {[['criticality', x('Criticidad', 'Criticality'), ['high', 'medium', 'low']],
                ['risk', x('Riesgo de sucesión', 'Succession risk'), ['high', 'medium', 'low']],
                ['horizon', x('Horizonte esperado', 'Expected horizon'), ['0_1y', '1_2y', '2y_plus', 'undetermined']]].map(([field, label, opts]) => (
                <div key={field} className="space-y-1.5">
                  <Label>{label}</Label>
                  <Select value={editForm[field]} onValueChange={(v) => setEditForm({ ...editForm, [field]: v })}>
                    <SelectTrigger data-testid={`succ-edit-${field}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {opts.map((o) => <SelectItem key={o} value={o}>{succLabel(isES, field === 'horizon' ? 'horizon' : field, o)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
              <div className="sm:col-span-2 space-y-1.5">
                <Label>{x('Impacto en el negocio', 'Business impact')}</Label>
                <Textarea data-testid="succ-edit-impact" rows={6} className="min-h-[9rem] max-h-[40vh] leading-relaxed" value={editForm.business_impact} onChange={(e) => setEditForm({ ...editForm, business_impact: e.target.value })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>{x('Cancelar', 'Cancel')}</Button>
            <Button onClick={savePosition} disabled={busy} className="bg-gradient-primary hover:opacity-90" data-testid="succ-edit-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : x('Guardar cambios', 'Save changes')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* add successor */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md" data-testid="succ-add-successor-dialog">
          <DialogHeader><DialogTitle>{x('Agregar sucesor potencial', 'Add potential successor')}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>{x('Colaborador/a', 'Employee')}</Label>
              <Select value={succForm.user_id} onValueChange={(v) => setSuccForm({ ...succForm, user_id: v })}>
                <SelectTrigger data-testid="succ-successor-picker"><SelectValue placeholder={x('Seleccionar…', 'Select…')} /></SelectTrigger>
                <SelectContent>
                  {employees.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{x('Preparación inicial', 'Initial readiness')}</Label>
              <Select value={succForm.readiness} onValueChange={(v) => setSuccForm({ ...succForm, readiness: v })}>
                <SelectTrigger data-testid="succ-successor-readiness"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {READINESS.map((r) => <SelectItem key={r} value={r}>{succLabel(isES, 'readiness', r)}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground">
                {x('La fecha objetivo de preparación se propone automáticamente a partir de la clasificación y puede ajustarse.', 'The readiness target date is proposed automatically from the classification and can be adjusted.')}
              </p>
            </div>
            <div className="space-y-1.5">
              <Label>{x('Notas', 'Notes')}</Label>
              <Textarea rows={2} value={succForm.notes} onChange={(e) => setSuccForm({ ...succForm, notes: e.target.value })} data-testid="succ-successor-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>{x('Cancelar', 'Cancel')}</Button>
            <Button onClick={addSuccessor} disabled={busy} className="bg-gradient-primary hover:opacity-90" data-testid="succ-successor-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : x('Agregar', 'Add')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* readiness review */}
      <Dialog open={!!reviewOpen} onOpenChange={(o) => !o && setReviewOpen(null)}>
        <DialogContent className="max-w-[95vw] sm:max-w-[640px]" data-testid="succ-review-dialog">
          <DialogHeader><DialogTitle>{x('Revisión formal de preparación', 'Formal readiness review')}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>{x('Preparación', 'Readiness')}</Label>
              <Select value={reviewForm.readiness} onValueChange={(v) => setReviewForm({ ...reviewForm, readiness: v })}>
                <SelectTrigger data-testid="succ-review-readiness"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {READINESS.map((r) => <SelectItem key={r} value={r}>{succLabel(isES, 'readiness', r)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{x('Comentario de la revisión (opcional)', 'Review comment (optional)')}</Label>
              <Textarea rows={7} className="min-h-[10rem] max-h-[45vh] leading-relaxed" value={reviewForm.comment} onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })} data-testid="succ-review-comment" />
              <p className="text-[11px] text-muted-foreground">
                {x('Documenta evidencia de avance, brechas pendientes, desarrollo completado y el razonamiento del cambio de preparación.',
                   'Document evidence of progress, remaining gaps, completed development and the reasoning behind the readiness change.')}
              </p>
            </div>
            <p className="text-[11px] text-muted-foreground">{x('La revisión conserva la línea de tiempo histórica y reprograma la próxima revisión.', 'The review preserves the historical timeline and reschedules the next review.')}</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewOpen(null)}>{x('Cancelar', 'Cancel')}</Button>
            <Button onClick={submitReview} disabled={busy} className="bg-gradient-primary hover:opacity-90" data-testid="succ-review-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : x('Registrar revisión', 'Record review')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* development action */}
      <Dialog open={!!devOpen} onOpenChange={(o) => !o && setDevOpen(null)}>
        <DialogContent className="max-w-md" data-testid="succ-dev-dialog">
          <DialogHeader><DialogTitle>{x('Nueva acción de desarrollo', 'New development action')}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {[['objective', x('Objetivo de desarrollo', 'Development objective')],
              ['action', x('Acción', 'Action')],
              ['competency', x('Competencia relacionada (opcional)', 'Related competency (optional)')]].map(([f, label]) => (
              <div key={f} className="space-y-1.5">
                <Label>{label}</Label>
                <Input value={devForm[f]} onChange={(e) => setDevForm({ ...devForm, [f]: e.target.value })} data-testid={`succ-dev-${f}`} />
              </div>
            ))}
            <div className="space-y-1.5">
              <Label>{x('Responsable', 'Responsible')}</Label>
              <Select value={devForm.responsible_user_id} onValueChange={(v) => setDevForm({ ...devForm, responsible_user_id: v })}>
                <SelectTrigger data-testid="succ-dev-responsible"><SelectValue placeholder={x('Seleccionar…', 'Select…')} /></SelectTrigger>
                <SelectContent>
                  {employees.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{x('Fecha objetivo', 'Target date')}</Label>
              <Input type="date" value={devForm.target_date} onChange={(e) => setDevForm({ ...devForm, target_date: e.target.value })} data-testid="succ-dev-target" />
            </div>
            {devForm.source_requirement && (
              <p className="text-[11px] text-muted-foreground">{x('Origen', 'Source')}: {succLabel(isES, 'quadrant', devForm.source_quadrant)} · {devForm.source_requirement}</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDevOpen(null)}>{x('Cancelar', 'Cancel')}</Button>
            <Button onClick={addDev} disabled={busy} className="bg-gradient-primary hover:opacity-90" data-testid="succ-dev-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : x('Crear acción', 'Create action')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
}
